﻿/**
 * FragmentRegistry
 * Owns mounted fragment references and registry access.
 *
 * This registry is shared by the Fragment and Orchestrator modules so mounted
 * fragment state has a single source of truth.
 */

import type { Fragment, FragmentName, FragmentState, PaymentMethod } from '@anddone/ajs-util';
import { FRAGMENT_CONFIG, DEFAULT_PAYMENT_METHOD } from '@anddone/ajs-util';

export class FragmentRegistry {
  private readonly fragments = new Map<FragmentName, Fragment>();
  private orchestratorFragment: Fragment | null = null;
  private currentPaymentMethod: PaymentMethod = DEFAULT_PAYMENT_METHOD;

  /**
   * Get a registered fragment by name.
   *
   * @param fragmentName - Name of the fragment
   * @returns Fragment | null
   */
  public get(fragmentName: FragmentName): Fragment | null {
    return this.fragments.get(fragmentName) ?? null;
  }

  /**
   * Register a fragment by name.
   *
   * @param fragmentName - Name of the fragment
   * @param fragment - Fragment reference
   * @returns void
   */
  public set(fragmentName: FragmentName, fragment: Fragment): void {
    this.fragments.set(fragmentName, fragment);
  }

  /**
   * Delete a registered fragment by name.
   *
   * @param fragmentName - Name of the fragment
   * @returns boolean
   */
  public delete(fragmentName: FragmentName): boolean {
    return this.fragments.delete(fragmentName);
  }

  /**
   * Remove all registered fragments and the orchestrator fragment reference.
   *
   * @returns void
   */
  public clear(): void {
    this.fragments.clear();
    this.orchestratorFragment = null;
  }

  /**
   * Get the number of registered non-orchestrator fragments.
   *
   * @returns number
   */
  public size(): number {
    return this.fragments.size;
  }

  /**
   * Get all registered fragment entries with the fragmentName as key.
   *
   * @returns Array of fragment entries
   */
  public entries(): Array<[FragmentName, Fragment]> {
    return Array.from(this.fragments.entries());
  }

  /**
   * Get all registered fragment entries with the fragmentName as key.
   *
   * @returns Array of fragment entries
   */
  public getFragments(): Fragment[] {
    return Array.from(this.fragments.values());
  }

  /**
   * Get all registered fragment states.
   *
   * Returns clone-safe fragment state objects without DOM references.
   *
   * @returns Array of fragment states
   */
  public getFragmentStates(): FragmentState[] {
    return Array.from(this.fragments.values()).map(fragment => ({
      id: fragment.id,
      status: fragment.status,
      fragmentName: fragment.fragmentName,
      error: fragment.error ? fragment.error : undefined,
      height: fragment.height,
      ...(fragment.values ? { values: fragment.values } : {}),
    }));
  }

  /**
   * Get all registered fragment names.
   *
   * @returns FragmentName[]
   */
  public names(): FragmentName[] {
    return Array.from(this.fragments.keys());
  }

  /**
   * Set the orchestrator fragment reference.
   *
   * @param fragment - Orchestrator fragment reference
   * @returns void
   */
  public setOrchestrator(fragment: Fragment): void {
    this.orchestratorFragment = fragment;
  }

  /**
   * Get the orchestrator fragment reference.
   *
   * @returns Fragment | null
   */
  public getOrchestrator(): Fragment | null {
    return this.orchestratorFragment;
  }

  /**
   * Clear the orchestrator fragment reference.
   *
   * @returns void
   */
  public clearOrchestrator(): void {
    this.orchestratorFragment = null;
  }

  /**
   * Update the active payment method used to filter processable fragments.
   *
   * @param paymentMethod - The new active payment method
   * @returns void
   */
  public setPaymentMethod(paymentMethod: PaymentMethod): void {
    this.currentPaymentMethod = paymentMethod;
  }

  /**
   * Get only fragments that participate in processing/submission.
   *
   * Excludes functional fragments (submit, orchestrator) and any fragment
   * whose paymentMethodTypeAllowed does not match the current payment method.
   *
   * @returns Array of processable fragment entries
   */
  public getProcessableFragments(): Array<[FragmentName, Fragment]> {
    return this.entries().filter(([fragmentName]) => this.isProcessableFragment(fragmentName));
  }

  /**
   * Get the names of fragments that participate in processing/submission.
   *
   * @returns Array of processable fragment entries
   */
  public getProcessableFragmentNames(): FragmentName[] {
    return Array.from(this.fragments.keys()).filter(fragmentName =>
      this.isProcessableFragment(fragmentName),
    );
  }

  /**
   * Check whether a fragment participates in processing/submission.
   *
   * A fragment is excluded when:
   * - it is not configured
   * - It is configure to have canSubmit=false
   * - its paymentMethodTypeAllowed is 'card' or 'ach' and does not match
   *   the current payment method.
   *
   * @param fragmentName - Name of the fragment
   * @returns boolean
   */
  private isProcessableFragment(fragmentName: string): boolean {
    const config = FRAGMENT_CONFIG.get(fragmentName as FragmentName);

    // If the fragment is not configured or cannot submit, it is not processable.
    if (!config || !config?.canSubmit) {
      return false;
    }

    const allowed = config?.paymentMethodTypeAllowed;

    if (!allowed || allowed === 'any') {
      return true;
    }

    return allowed === this.currentPaymentMethod;
  }
}
